import { getPhasePosition } from '@/allocation/allocation';
import {
    getUnstyledAllocationVariantIndex,
    getUnstyledAllocationVariantLabel,
} from '@/components/AllocationVariantIndex/getFullAllocationVariantIndex';
import { DEFAULT_HIGHLIGHT_VALUE } from '@/components/AllocationVariantSelectionPanel/types';
import { BadgeDropDownValue } from '@/components/BadgeDropDown/BadgeDropDown.vue';
import { mapStringToSpecializationType } from '@/components/Timeline/allocation/SpecializationMappings';
import { TimelineVariant } from '@/components/Timeline/allocation/types';
import { getDurationInDays } from '@/components/Timeline/logic/durations';
import { mapToPhaseColor } from '@/components/Timeline/PhaseColorMappings';
import { getTransparencyVariant } from '@/components/Timeline/timeline';
import { Allocation, LocationTypeCategory, PhaseTypeDatabase } from '@/components/Timeline/Timeline.types';
import { EMPTY_VALUE_STRING } from '@/forms/forms';
import {
    EventBaseDataFragment,
    EventVariantDataFragment,
    ExternalEventAllocationUsageType,
    GetEventsQuery,
    TimelineEventAllocationAreaFragment,
    TimelineEventAllocationRoomFragment,
    TimelineEventVariantFragment,
} from '@/generated/graphql';
import { $t } from '@/plugins/fluent';
import router from '@/plugins/router';
import { insideRangeCheck } from '@/utils/date';
import { NodesType } from '@/utils/graphql';
import { getVariantRoute } from '@/variant/utils';
import { useFluent } from 'fluent-vue';
import { DateTime } from 'luxon';
import {
    eventSeriesStates,
    EventSeriesStatus,
    eventStates,
    EventStatus,
    eventVariantStates,
    EventVariantStatus,
} from './types';

export type EventVariant = {
    id: string;
    name: string;
};

export type SelectableEventVariant = {
    id: string;
    name: string;
    isCurrent: boolean;
    start: string;
    end: string;
    status?: string;
};

export type Event = {
    id: string;
    name: string;
    description?: string;
    mainVariant: EventVariant;
};

export type EventSeries = {
    id: string;
    nameShort: string;
    events?: Event[];
};

export type EventUpdate = {
    id: string;
    name?: string;
    description?: string;
    iconUpload?: string;
    iconId?: string;
};

export type EventVariantDetails = EventVariant & {
    isCurrent: boolean;
    start: string;
    end: string;
    version: number;
    isArchived: boolean;
};

export function iconForVariant({ isCurrent, isArchived }: { isCurrent: boolean; isArchived: boolean }) {
    if (isCurrent) return 'star';
    if (isArchived) return 'inventory_2';
    return 'star_border';
}

type Variant = { name: string; version: number };
export function nameForVariant({ name, version }: Variant) {
    const realName = name === '' ? $t('event-first-variant-name') : name;
    return $t('event-variant-name', { name: realName, version: version + 1 });
}

export function fullNameForVariant(eventName: string, { name, version }: Variant) {
    return `${eventName}: ${nameForVariant({ name, version })}`;
}

type QueryEventType = NodesType<GetEventsQuery['events']>;
type QueryVariantType = NodesType<QueryEventType['mainVariant']>;

function toVariantItem(variant: QueryVariantType) {
    return {
        id: variant.id,
        name: variant.name,
    };
}

export function toEventItem(event: QueryEventType) {
    return {
        id: event.id,
        name: event.nameShort,
        mainVariant: toVariantItem(event.mainVariant.nodes[0]),
    };
}

export function toSeriesItem(series: NodesType<GetEventsQuery['eventSerieses']>, events: QueryEventType[] | undefined) {
    let seriesEvents: QueryEventType[] = [];
    if (events) {
        seriesEvents = events.filter((e: QueryEventType) => e.series?.id === series.id);
    }
    return {
        id: series.id,
        nameShort: series.nameShort,
        singleEvent: series.singleEvent,
        events: seriesEvents.map((e) => toEventItem(e)),
    };
}

export function getEventMainRoute(eventId: string) {
    return { name: 'event-masterdata-tab', params: { eventId } };
}

export function goToEvent(eventId: string) {
    router.push(getEventMainRoute(eventId));
}

export function getEventAllocationsRoute(eventId: string) {
    return { name: 'event-allocations-tab', params: { eventId } };
}

export function getCreateEventLink(seriesId: string, seriesName: string) {
    return { name: 'add-event', query: { seriesId, seriesName } };
}

export function getCreateEventSeriesLink() {
    return { name: 'add-event-series' };
}

export function getEventSeriesLink(seriesId: string) {
    return { name: 'event-series-overview', params: { seriesId } };
}

export function goToEventSeries(seriesId: string) {
    router.push(getEventSeriesLink(seriesId));
}

export function getEventVariantRoute(eventId: string, variantIndex: number) {
    return { name: 'event-variants-tab', params: { eventId }, hash: `#v${variantIndex}` };
}

export function getEventVariantLinkTitle(event: EventBaseDataFragment, variant: EventVariantDataFragment) {
    const index = getUnstyledAllocationVariantIndex(variant.index);
    if (variant.name === '') {
        return `${index} - ${event.nameShort} - ${event.series?.nameShort}`;
    }
    return `${index} (${variant.name}) - ${event.nameShort} - ${event.series?.nameShort}`;
}

export function idToName(id: string, items: { id: string; name: string }[]) {
    if (id === '') return EMPTY_VALUE_STRING;
    const item = items.find((i) => i.id === id);
    if (item) {
        return item.name;
    }
    throw new Error(`No item name found for id: ${id}`);
}

export function nullBooleanToString(value: boolean | undefined) {
    const fluent = useFluent();
    if (value === undefined || value === null) return EMPTY_VALUE_STRING;
    return value ? fluent.$t('yes') : fluent.$t('no');
}

export function eventStatusToBadgeVariant(status: string) {
    switch (status as EventStatus) {
        case 'active-option':
            return 'Green';
        case 'active-fix':
            return 'Green';
        case 'active-idea':
            return 'Green';
        case 'passive':
            return 'Yellow';
        case 'canceled':
            return 'Purple';
        case 'completed':
            return 'Gray';
        case 'discontinued':
            return 'Red';
        default:
            return 'Green';
    }
}

export function eventSeriesStatusToBadgeVariant(status: string) {
    switch (status as EventSeriesStatus) {
        case 'active':
            return 'Green';
        case 'passive':
            return 'Yellow';
        case 'completed':
            return 'Gray';
        case 'discontinued':
            return 'Red';
        default:
            return 'Green';
    }
}

export function eventVariantStatusToBadgeVariant(status: string) {
    switch (status as EventVariantStatus) {
        case 'active-1':
            return 'Green';
        case 'active-2':
            return 'Green';
        case 'passive':
            return 'Yellow';
        case 'discontinued':
            return 'Red';
        default:
            return 'Green';
    }
}

export function getEventStatusLabel(status: string) {
    return $t('event-status', { status });
}

export function getEventSeriesStatusLabel(status: string) {
    return $t('event-series-status', { status });
}

export function getEventVariantStatusLabel(status: string) {
    return $t('event-variant-status', { status });
}

export function getEventStatusComboboxItems(): { id: EventStatus; name: string }[] {
    return eventStates.map((s) => ({
        id: s,
        name: getEventStatusLabel(s),
    }));
}

export function getEventSeriesStatusItems(status: EventSeriesStatus): (BadgeDropDownValue & { isSelected: boolean })[] {
    return eventSeriesStates.map((s) => ({
        id: s,
        label: getEventSeriesStatusLabel(s),
        variant: eventSeriesStatusToBadgeVariant(s),
        isSelected: status === s,
    }));
}

export function getEventSeriesStatusItem(status: EventSeriesStatus): BadgeDropDownValue {
    return {
        id: status,
        label: getEventSeriesStatusLabel(status),
        variant: eventSeriesStatusToBadgeVariant(status),
    };
}

export function getEventStatusItems(status: EventStatus): (BadgeDropDownValue & { isSelected: boolean })[] {
    return eventStates.map((s) => ({
        id: s,
        label: getEventStatusLabel(s),
        variant: eventStatusToBadgeVariant(s),
        isSelected: status === s,
    }));
}

export function getEventStatusItem(status: EventStatus): BadgeDropDownValue {
    return { id: status, label: getEventStatusLabel(status), variant: eventStatusToBadgeVariant(status) };
}

export function getEventVariantStatusItems(
    status: EventVariantStatus,
): (BadgeDropDownValue & { isSelected: boolean })[] {
    return eventVariantStates.map((s) => ({
        id: s,
        label: getEventVariantStatusLabel(s),
        variant: eventVariantStatusToBadgeVariant(s),
        isSelected: status === s,
    }));
}

export function getEventVariantStatusItem(status: EventVariantStatus): BadgeDropDownValue {
    return {
        id: status,
        label: getEventVariantStatusLabel(status),
        variant: eventVariantStatusToBadgeVariant(status),
    };
}

export type TimelineEventVariantAllocationData = {
    id: string;
    index: number;
    name: string;
    referenceAllocationRoomId: string | null;
    referenceAllocationAreaId: string | null;
    isCurrent: boolean;
    event: {
        id: string;
        nameShort: string;
    };
};

export function mapEventAllocationForTimeline(
    variant: TimelineEventVariantAllocationData,
    allocation: TimelineEventAllocationRoomFragment | TimelineEventAllocationAreaFragment,
    locationTypeCategory: LocationTypeCategory,
    executionPhasePosition: number,
    areAllocationsEditable: boolean,
): Allocation {
    const referenceAllocationId = variant.referenceAllocationRoomId ?? variant.referenceAllocationAreaId ?? null;
    const allocationStart = DateTime.fromISO(allocation.start);
    const allocationEnd = DateTime.fromISO(allocation.end);

    return {
        id: allocation.id,
        allocationType: 'Event',
        allocationVariant: {
            id: variant.id,
            index: variant.index,
            label: variant.name,
            target: getVariantRoute('Event', variant.event.id, variant.index),
            referenceAllocationId,
        },
        label: $t('allocation-bar-label', {
            groupLabel: variant.event.nameShort,
            variantLabel: getUnstyledAllocationVariantLabel(variant.index, variant.name),
        }),
        variantLabel: getUnstyledAllocationVariantLabel(variant.index, variant.name),
        groupLabel: variant.event.nameShort,
        phases: allocation.phases.nodes.map((phase) => {
            const offsetDays = getDurationInDays(phase.offset);
            const durationDays = getDurationInDays(phase.duration);
            return {
                id: phase.id,
                startDateTime: allocationStart.plus({ days: offsetDays }),
                endDateTime: allocationStart.plus({ days: offsetDays + durationDays }),
                cellSpan: durationDays,
                position: phase.usage.isExecution
                    ? 'Main'
                    : getPhasePosition(executionPhasePosition, phase.usage.position),
                specialization: mapStringToSpecializationType(phase.specializationType),
                color: mapToPhaseColor(
                    phase.usage.type as Exclude<PhaseTypeDatabase, ExternalEventAllocationUsageType>,
                ),
                usage: {
                    id: phase.usage.id,
                    type: phase.usage.type as PhaseTypeDatabase,
                    position: phase.usage.position,
                },
            };
        }),
        startDateTime: allocationStart,
        endDateTime: allocationEnd,
        locationId: allocation.locationId,
        clickAction: areAllocationsEditable
            ? { type: 'Edit' as const }
            : {
                  type: 'Open-Url' as const,
                  tooltipText: $t('click-to-route-to-masterdata'),
                  target: getEventVariantRoute(variant.event.id, variant.index),
              },
        linkTarget: getEventMainRoute(variant.event.id),
        isOptional: allocation.isOptional,
        variant: getTransparencyVariant(DEFAULT_HIGHLIGHT_VALUE, variant.id, variant.isCurrent),
        isInside: locationTypeCategory === 'Room' ? true : false,
        locationTypeCategory,
    };
}

export function mapEventVariantForTimeline(
    variant: TimelineEventVariantFragment,
    timelineStart: DateTime | undefined,
    timelineEnd: DateTime | undefined,
    executionPhasePosition: number,
    areAllocationsEditable: boolean,
): TimelineVariant {
    const mappedVariant = {
        ...variant,
        referenceAllocationRoomId: variant.referenceAllocationRoomId ?? null,
        referenceAllocationAreaId: variant.referenceAllocationAreaId ?? null,
    };
    const allocationsTotalCount = variant.rooms.totalCount + variant.areas.totalCount;
    if (timelineStart && timelineEnd) {
        return {
            id: variant.id,
            index: variant.index,
            isCurrent: variant.isCurrent,
            name: variant.name,
            referenceAllocationId: variant.referenceAllocationAreaId ?? variant.referenceAllocationRoomId ?? null,
            status: variant.status as EventVariantStatus,
            allocationsTotalCount,
            allocations: {
                nodes: [
                    ...(variant.rooms?.nodes
                        .filter((n) =>
                            insideRangeCheck(
                                DateTime.fromISO(n.start),
                                DateTime.fromISO(n.end),
                                timelineStart,
                                timelineEnd,
                            ),
                        )
                        .map((n) =>
                            mapEventAllocationForTimeline(
                                mappedVariant,
                                n,
                                'Room',
                                executionPhasePosition,
                                areAllocationsEditable,
                            ),
                        ) ?? []),
                    ...(variant.areas?.nodes
                        .filter((n) =>
                            insideRangeCheck(
                                DateTime.fromISO(n.start),
                                DateTime.fromISO(n.end),
                                timelineStart,
                                timelineEnd,
                            ),
                        )
                        .map((n) =>
                            mapEventAllocationForTimeline(
                                mappedVariant,
                                n,
                                'Area',
                                executionPhasePosition,
                                areAllocationsEditable,
                            ),
                        ) ?? []),
                ],
            },
        };
    }
    return {
        ...variant,
        referenceAllocationId: variant.referenceAllocationAreaId ?? variant.referenceAllocationRoomId ?? null,
        status: variant.status as EventVariantStatus,
        allocationsTotalCount,
        allocations: {
            nodes: [
                ...(variant.rooms?.nodes.map((n) =>
                    mapEventAllocationForTimeline(
                        mappedVariant,
                        n,
                        'Room',
                        executionPhasePosition,
                        areAllocationsEditable,
                    ),
                ) ?? []),
                ...(variant.areas?.nodes.map((n) =>
                    mapEventAllocationForTimeline(
                        mappedVariant,
                        n,
                        'Area',
                        executionPhasePosition,
                        areAllocationsEditable,
                    ),
                ) ?? []),
            ],
        },
    };
}
